import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import Switch from '@mui/material/Switch';
import { useEffect, useRef, useState, startTransition } from 'react';
import { Site, SiteUpdate, UserRole } from 'src/api/Api';
import {
  Delete,
  Error,
  HighlightAlt,
} from '@mui/icons-material';
import { AnalysisStatus, SampleProjectId } from 'src/enum';
import { useNavigate } from 'react-router-dom';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import { carbonAnalysisMessages, biodiversityAnalysisMessages, environmentalAnalysisMessages } from '../../constants/loaderMessages';
import SiteMapWithLayer from '../DMap/SiteMapWithLayer';
import clientService from 'src/clientService';
import { useAppState } from 'src/state-management/state-provider';
import LinearWithValueLabel from '../components/LinearProgressBar';
import { PermissionGuard } from 'src/auth/PermissionGuard';
import { ProjectWithSites } from 'src/state-management/state';

const standardDuration = 90; // 90 seconds

// Calculate initial progress based on elapsed time since analysis started
// Uses updated_at as a proxy for when analysis might have started
const calculateInitialProgress = (updatedAt: string | undefined, duration: number): number => {
  if (!updatedAt) return 0;
  
  const updatedTime = new Date(updatedAt).getTime();
  const now = Date.now();
  const elapsedSeconds = Math.floor((now - updatedTime) / 1000);
  
  // Only calculate progress if timestamp is recent (within duration window)
  // This prevents showing incorrect progress from stale timestamps
  if (elapsedSeconds < 0 || elapsedSeconds > duration) {
    return 0;
  }
  
  // Calculate progress: (elapsed / duration) * 95, capped at 95%
  const progress = Math.min((elapsedSeconds / duration) * 95, 95);
  return Math.max(0, progress);
};

const SiteCard = ({
  site,
  project,
  onDelete,
  triggerAnalysis,
}: {
  site: Site;
  project: ProjectWithSites;
  onDelete: () => void;
  triggerAnalysis: (update: SiteUpdate) => void;
}) => {
  const { state, dispatch } = useAppState();
  const isAdmin = state.user?.role.includes(UserRole.Admin);
  const isSampleProject = project?.project_id === SampleProjectId;
  const canDelete = isAdmin || !isSampleProject;
  const navigate = useNavigate();
  const [currentSite, setCurrentSite] = useState<Site>(site);
  const [isEnvEnabled, setIsEnvEnabled] = useState(
    site.env_analysis_enabled || false,
  );
  const isPolling = useRef(false);
  const prevSite = useRef<Site>(site);

  const [isBioFinishing, setIsBioFinishing] = useState(false);
  const [isCarbonFinishing, setIsCarbonFinishing] = useState(false);
  const [isEnvFinishing, setIsEnvFinishing] = useState(false);

  useEffect(() => {
    // Reset finishing states when analysis status is no longer PENDING
    // This ensures the progress bar stops immediately when backend responds
    if (currentSite.bio_analysis_status !== AnalysisStatus.PENDING) {
      setIsBioFinishing(false);
    }
    if (currentSite.carbon_analysis_status !== AnalysisStatus.PENDING) {
      setIsCarbonFinishing(false);
    }
    if (currentSite.env_analysis_status !== AnalysisStatus.PENDING) {
      setIsEnvFinishing(false);
    }

    prevSite.current = currentSite;
  }, [currentSite]);

  useEffect(() => {
    setCurrentSite(site);
  }, [site]);

  useEffect(() => {
    const isPollingNeeded =
      currentSite.bio_analysis_status === AnalysisStatus.PENDING ||
      currentSite.carbon_analysis_status === AnalysisStatus.PENDING ||
      currentSite.env_analysis_status === AnalysisStatus.PENDING;

    if (!isPollingNeeded) {
      return;
    }

    const intervalId = setInterval(async () => {
      if (isPolling.current) {
        return;
      }
      try {
        isPolling.current = true;
        const response = await clientService.api.readSiteApiV1SitesSiteIdGet(
          currentSite.site_id,
        );
        if (response.data) {
          setCurrentSite(response.data);
          startTransition(() => {
            // Update the site in the project details
            if (project) {
              dispatch({
                type: 'SET_PROJECT_DETAILS',
                payload: {
                  ...project,
                  sites: project.sites?.map((s) =>
                    s.site_id === response.data.site_id ? response.data : s,
                  ),
                } as ProjectWithSites,
              });
            }
          });
        }
      } catch (error) {
        console.error('Failed to fetch site status:', error);
      } finally {
        isPolling.current = false;
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(intervalId);
  }, [currentSite, project, dispatch]);

  useEffect(() => {
    setIsEnvEnabled(currentSite.env_analysis_enabled || false);
  }, [currentSite.env_analysis_enabled]);

  const handleEnvAnalysisToggle = () => {
    const newIsEnabled = !isEnvEnabled;
    if (newIsEnabled) {
        setIsEnvEnabled(true);
        triggerAnalysis({ env_analysis_enabled: true });
    } else {
      setIsEnvEnabled(false);
      triggerAnalysis({ env_analysis_enabled: false });
    }
  };

  const handleNavigateToDashboard = (type: 'bio' | 'carbon' | 'climate') => {
    startTransition(() => {
      switch (type) {
        case 'bio':
          navigate(`/project/${project.project_id}/bio-diversity/site/${currentSite.site_id}`);
          break;
        case 'carbon':
          navigate(`/project/${project.project_id}/carbon/site/${currentSite.site_id}`);
          break;
        case 'climate':
          navigate(`/project/${project.project_id}/climate/site/${currentSite.site_id}`);
          break;
      }
    });
  };

  const boxStyle = {
    backgroundColor: '#f8f8f8',
    padding: '8px',
    borderRadius: '10px',
    marginTop: '10px',
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#fff' }}>
      <CardHeader 
        title={currentSite.site_name}
        titleTypographyProps={{ variant: 'h6', noWrap: true }}
      />
      <Divider />
      <Box sx={{ height: 200, position: 'relative' }}>
        <SiteMapWithLayer
          sites={[currentSite]}
          fitToBoundary={true}
          hideAllControl={true}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={2}
          sx={{ pb: 1 }}
        >
          {currentSite.area && (
            <Typography
              color="text.secondary"
              variant="body2"
            >
              {roundToTwoDecimalPlaces(currentSite.area)} {project.area_unit || 'ha'}
            </Typography>
          )}
        </Box>

        {project.has_biodiversity_module && (
          <Box sx={boxStyle}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" fontWeight="medium">
                Biodiversity Analysis
              </Typography>
              {currentSite.bio_analysis_status !== AnalysisStatus.COMPLETED && (
                <Switch
                  size="small"
                  checked={currentSite.bio_analysis_enabled || false}
                  onChange={() =>
                    triggerAnalysis({
                      bio_analysis_enabled: !currentSite.bio_analysis_enabled,
                    })
                  }
                />
              )}
              {currentSite.bio_analysis_enabled &&
                currentSite.bio_analysis_status === AnalysisStatus.COMPLETED && (
                  <Button
                    size="small"
                    onClick={() => handleNavigateToDashboard('bio')}
                    endIcon={<HighlightAlt />}
                  >
                    Dashboard
                  </Button>
                )}
            </Box>

            {currentSite.bio_analysis_enabled && (
              <Box mt={1}>
                {currentSite.bio_analysis_status === AnalysisStatus.PENDING && (
                  <LinearWithValueLabel
                    duration={standardDuration}
                    isFinishing={isBioFinishing}
                    onFinished={() => setIsBioFinishing(false)}
                    message={biodiversityAnalysisMessages}
                    isActive={currentSite.bio_analysis_status === AnalysisStatus.PENDING}
                    initialProgress={calculateInitialProgress(currentSite.updated_at, standardDuration)}
                  />
                )}
                {currentSite.bio_analysis_status === AnalysisStatus.ERROR && (
                  <Chip
                    label="Error"
                    size="small"
                    color="error"
                    icon={<Error />}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {project.has_carbon_module && (
          <Box sx={boxStyle}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" fontWeight="medium">
                Carbon Analysis
              </Typography>
              {currentSite.carbon_analysis_status !== AnalysisStatus.COMPLETED && (
                <Switch
                  size="small"
                  checked={currentSite.carbon_analysis_enabled || false}
                  onChange={() =>
                    triggerAnalysis({
                      carbon_analysis_enabled: !currentSite.carbon_analysis_enabled,
                    })
                  }
                />
              )}
              {currentSite.carbon_analysis_enabled &&
                currentSite.carbon_analysis_status === AnalysisStatus.COMPLETED && (
                  <Button
                    size="small"
                    onClick={() => handleNavigateToDashboard('carbon')}
                    endIcon={<HighlightAlt />}
                  >
                    Dashboard
                  </Button>
                )}
            </Box>

            {currentSite.carbon_analysis_enabled && (
              <Box mt={1}>
                {currentSite.carbon_analysis_status === AnalysisStatus.PENDING && (
                  <LinearWithValueLabel
                    duration={standardDuration}
                    isFinishing={isCarbonFinishing}
                    onFinished={() => setIsCarbonFinishing(false)}
                    message={carbonAnalysisMessages}
                    isActive={currentSite.carbon_analysis_status === AnalysisStatus.PENDING}
                    initialProgress={calculateInitialProgress(currentSite.updated_at, standardDuration)}
                  />
                )}
                {currentSite.carbon_analysis_status === AnalysisStatus.ERROR && (
                  <Chip
                    label="Error"
                    size="small"
                    color="error"
                    icon={<Error />}
                  />
                )}
              </Box>
            )}
          </Box>
        )}

        {project.has_environmental_module && (
          <Box sx={boxStyle}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="body2" fontWeight="medium">
                Climate Analysis
              </Typography>
              {currentSite.env_analysis_status !== AnalysisStatus.COMPLETED && (
                <Switch
                  size="small"
                  checked={isEnvEnabled}
                  onChange={handleEnvAnalysisToggle}
                />
              )}
              {currentSite.env_analysis_enabled &&
                currentSite.env_analysis_status === AnalysisStatus.COMPLETED && (
                  <Button
                    size="small"
                    onClick={() => handleNavigateToDashboard('climate')}
                    endIcon={<HighlightAlt />}
                  >
                    Dashboard
                  </Button>
                )}
            </Box>

            {isEnvEnabled && (
              <Box mt={1}>
                {currentSite.env_analysis_status === AnalysisStatus.PENDING && (
                  <LinearWithValueLabel
                    duration={standardDuration}
                    isFinishing={isEnvFinishing}
                    onFinished={() => setIsEnvFinishing(false)}
                    message={environmentalAnalysisMessages}
                    isActive={currentSite.env_analysis_status === AnalysisStatus.PENDING}
                    initialProgress={calculateInitialProgress(currentSite.updated_at, standardDuration)}
                  />
                )}
                {currentSite.env_analysis_status === AnalysisStatus.ERROR && (
                  <Chip
                    label="Error"
                    size="small"
                    color="error"
                    icon={<Error />}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </CardContent>
      
      <Divider />
      <PermissionGuard resourceType="project" permission="delete" resource={project}>
        <CardActions>
          <Box display="flex" justifyContent="flex-start" width="100%">
            {canDelete && (
                <IconButton
                color="error"
                aria-label="Delete this site"
                size="small"
                onClick={onDelete}
                >
                <Delete />
                </IconButton>
            )}
          </Box>
        </CardActions>
      </PermissionGuard>
    </Card>
  );
};

export default SiteCard;